import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

import { useEffect } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { ResponsiveMascot } from "@/components/animations/mascot";
import { ChatCard } from "@/components/card/chat";
import { PageHeader } from "@/components/layout/heading";
import { Skeleton } from "@/components/ui/skeleton";

import { ChatApiFactory } from "@/apis/chat";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { ChatSession } from "@/schema/chat";

export const Route = createFileRoute("/dashboard/_layout/chats")({
  component: RouteComponent,
});

const mPageSize = 20;
const mGutterBreakpoints = { 320: 20 };
const mColumnBreakpoints = { 320: 1, 768: 2, 1024: 3, 1280: 4 };

function RouteComponent() {
  const {
    data: chats = [],
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ChatApiFactory.Keys.FetchInfiniteChatSessions({}),
    queryFn: ({ pageParam }) => {
      return ChatApiFactory.Apis.FetchChatSessions({ page: pageParam, size: mPageSize });
    },
    getNextPageParam: (page) => {
      return page.page < page.pages ? page.page + 1 : null;
    },
    select: (data) => data.pages.flatMap((page) => page.items),
  });

  return (
    <section id="chats" className="@container">
      <PageHeader title="Chats" description="Explore every chat you've had with our agents" className="mb-8" />
      <div>
        {!chats.length ? (
          isLoading ? (
            <div className="grid grid-cols-1 @md:grid-cols-2 @xl:grid-cols-3 @3xl:grid-cols-4 @6xl:grid-cols-5 gap-5">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-96 w-full" />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-10 text-center border border-dashed rounded-2xl">
              <ResponsiveMascot mascot="MascotError" className="size-64 md:size-80 relative mx-auto" />
              <p className="text-base font-medium -mt-10 mb-1">No chats found</p>
              <p className="text-sm text-muted-foreground">Chats will appear here once you start a new chat</p>
            </div>
          )
        ) : (
          <ChatMasonryGrid chats={chats} canLoadMore={hasNextPage} onLoadMore={fetchNextPage} />
        )}
      </div>
    </section>
  );
}

interface ChatMasonryGridProps {
  chats: ChatSession[];
  canLoadMore: boolean;
  onLoadMore: () => void;
}

function ChatMasonryGrid({ chats, canLoadMore, onLoadMore }: ChatMasonryGridProps) {
  const [ref, entry] = useIntersectionObserver({
    root: null,
    threshold: 0,
    rootMargin: "50% 0px 0px 0px",
  });

  const visible = !!entry?.isIntersecting;

  useEffect(() => {
    if (!visible || !canLoadMore) return;
    onLoadMore();
  }, [visible, canLoadMore, onLoadMore]);

  return (
    <div className="relative">
      <ResponsiveMasonry columnsCountBreakPoints={mColumnBreakpoints} gutterBreakPoints={mGutterBreakpoints}>
        <Masonry>
          {chats.map((chat) => (
            <ChatCard key={chat.id} chat={chat} />
          ))}
        </Masonry>
      </ResponsiveMasonry>
      <div ref={ref} className="flex items-center justify-center gap-2.5 pt-12 pb-4">
        {canLoadMore ? <div className="spinner size-5 !border-primary" /> : null}
        <span className="text-sm font-medium text-muted-foreground">{canLoadMore ? "Loading more assets..." : "End of results"}</span>
      </div>
    </div>
  );
}
